import { PERMISSIONS } from '@ibe/constants';
import { Button, ConfirmDialog, StatusBadge, toneForStatus } from '@ibe/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PermissionGuard } from '../../../auth/permission-guard.js';

import { DetailField, DetailGrid } from '../../../components/resource-detail/detail-grid.js';
import { ResourceDetailPage } from '../../../components/resource-detail/resource-detail-page.js';
import { useSoftDeletePromotion } from '../api/mutations.js';
import { usePromotion } from '../api/queries.js';
import { type PromotionDetail } from '../api/schemas.js';

const PERCENT_MULTIPLIER = 100;

function percentDisplay(promotion: PromotionDetail): string | null {
  if (promotion.percentage === null) return null;
  const [num, den] = promotion.percentage.split('/');
  if (num === undefined || den === undefined) return promotion.percentage;
  const value = (Number(num) / Number(den)) * PERCENT_MULTIPLIER;
  return `${String(value)}%`;
}

function moneyDisplay(amount: string | null, currency: string): string | null {
  if (amount === null) return null;
  return `${amount} ${currency}`;
}

export function PromotionDetailPage(): React.JSX.Element {
  const promotionId = useParams().promotionId ?? '';
  const navigate = useNavigate();
  const query = usePromotion(promotionId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const softDelete = useSoftDeletePromotion();

  const onDelete = async (): Promise<void> => {
    await softDelete.mutateAsync(promotionId);
    setConfirmingDelete(false);
    void navigate('/promotions');
  };

  return (
    <ResourceDetailPage
      query={query}
      permission={PERMISSIONS.PROMOTION_READ}
      parent={{ to: '/promotions', label: 'Promotions' }}
      title={(data) => data.name}
      actions={
        <>
          <PermissionGuard permission={PERMISSIONS.PROMOTION_UPDATE}>
            <Button asChild variant="outline">
              <Link to={`/promotions/${promotionId}/edit`}>Edit</Link>
            </Button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.PROMOTION_DELETE}>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmingDelete(true);
              }}
            >
              Delete
            </Button>
          </PermissionGuard>
        </>
      }
    >
      {(data) => (
        <>
          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title="Delete promotion"
            description={`${data.name} will be removed. Its audit history and any bookings that used it are retained.`}
            confirmLabel="Delete"
            destructive
            loading={softDelete.isPending}
            onConfirm={() => {
              void onDelete();
            }}
          />

          <DetailGrid>
            <DetailField label="Code">{data.code}</DetailField>
            <DetailField label="Type">{data.type}</DetailField>
            <DetailField label="Status">
              <StatusBadge tone={toneForStatus(data.enabled ? 'ACTIVE' : 'INACTIVE')}>
                {data.enabled ? 'Active' : 'Inactive'}
              </StatusBadge>
            </DetailField>
            <DetailField label="Priority">{String(data.priority)}</DetailField>
            <DetailField label="Percentage">{percentDisplay(data) ?? '—'}</DetailField>
            <DetailField label="Amount off">
              {moneyDisplay(data.amount, data.currency) ?? '—'}
            </DetailField>
            <DetailField label="Fixed rate">
              {moneyDisplay(data.fixedRate, data.currency) ?? '—'}
            </DetailField>
            <DetailField label="Currency">{data.currency}</DetailField>
            <DetailField label="Platform">{data.platform}</DetailField>
            <DetailField label="Combinable">{data.combinable ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Exclusive">{data.exclusive ? 'Yes' : 'No'}</DetailField>
            <DetailField label="Promo code required">
              {data.promoCodeRequired ? 'Yes' : 'No'}
            </DetailField>
          </DetailGrid>
        </>
      )}
    </ResourceDetailPage>
  );
}
